// ScopePanel.js — the takeoff "Scope & Frequency" panel, lifted from the legacy
// TakeoffEstimateWorkspace so the Service Flavors view can show + edit the real
// per-task scope (coverage / frequency chips, role toggles) alongside the flavors.
// Presentational + prop-driven. Reuses the legacy te-* CSS classes.
import React from 'react';
import { getFrequency, getVisitsPerYear, computeService } from '../estimateEngine';
import '../takeoffEstimateWorkspace.css';

const fmtCurrency = (value, maxDigits = 0) =>
  (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    maximumFractionDigits: maxDigits, minimumFractionDigits: maxDigits,
  });

const fmtNumber = (value, maxDigits = 1) =>
  (Number(value) || 0).toLocaleString('en-US', {
    maximumFractionDigits: maxDigits, minimumFractionDigits: maxDigits,
  });

const roleClass = { Maint: 'maint', Arborist: 'arb', Herbicide: 'herb', Irrigation: 'irrig' };

const FREQ_CHOICES = ['weekly', 'biweekly', 'monthly', 'bimonthly', 'quarterly', '3x-yr', '2x-yr', 'annual'];
const COVERAGE_CHOICES = [0.25, 0.5, 0.75, 1];

const frequencyKey = (frequency, settings) => {
  if (typeof frequency === 'string') return frequency;
  const visits = getFrequency(frequency, settings).visitsPerYear;
  return FREQ_CHOICES.find((key) => getFrequency(key, settings).visitsPerYear === visits) || 'monthly';
};

const Button = ({ className = '', children, ...props }) => (
  <button type="button" className={`te-btn ${className}`} {...props}>{children}</button>
);

const FrequencyPill = ({ frequency, settings, onChange }) => {
  const preset = getFrequency(frequency, settings);
  if (!onChange) {
    return <span className={`te-freq-pill ${preset.family || 'yr'}`}>{preset.label}</span>;
  }
  const value = frequencyKey(frequency, settings);
  return (
    <select
      aria-label="Frequency"
      className={`te-freq-pill te-chip-select ${preset.family || 'yr'}`}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {FREQ_CHOICES.map((key) => (
        <option key={key} value={key}>{getFrequency(key, settings).label}</option>
      ))}
    </select>
  );
};

const CoveragePill = ({ coverage, onChange }) => {
  const value = Number(coverage) || 0;
  const pct = Math.round(value * 100);
  const full = pct >= 100;
  if (!onChange) {
    return <span className={`te-cov ${full ? 'full' : 'partial'}`}>{full ? '100%' : `${pct}% cov`}</span>;
  }
  return (
    <select
      aria-label="Coverage"
      className={`te-cov te-chip-select ${full ? 'full' : 'partial'}`}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {COVERAGE_CHOICES.map((c) => (
        <option key={c} value={c}>{c >= 1 ? '100%' : `${Math.round(c * 100)}% cov`}</option>
      ))}
    </select>
  );
};

const TaskRow = ({ task, settings, onRateChange }) => {
  const visits = getVisitsPerYear(task.frequency, settings);
  const annual = Number(task.annualHours) || (Number(task.hoursPerVisit) || 0) * visits;
  const editable = !!onRateChange && !!task.serviceCode;
  return (
    <div className={`te-task ${task.flag ? 'flag' : ''}`}>
      <div className="lbl">
        {task.label}
        <span className="areas">({task.areaCount || 0}{task.serviceCode === 'irrigation_check' ? ' zones' : ' areas'})</span>
      </div>
      <div className="h">
        {fmtNumber(task.hoursPerVisit, 2)}h{' '}
        <CoveragePill
          coverage={task.coverage ?? 1}
          onChange={editable ? ((coverage) => onRateChange(task.serviceCode, { coverage })) : undefined}
        />
        {visits < 52 && <> x <FrequencyPill
          frequency={task.frequency}
          settings={settings}
          onChange={editable ? ((frequency) => onRateChange(task.serviceCode, { frequency })) : undefined}
        /></>}
      </div>
      <div className="ann">x {fmtNumber(visits, 0)} = {fmtNumber(annual, 1)}</div>
    </div>
  );
};

const FrequencyGroup = ({ title, frequency, tasks, settings, note, onRateChange }) => {
  if (!tasks?.length) return null;
  return (
    <div className="te-freq-group">
      <div className="te-freq-head">
        {title}
        {frequency && <FrequencyPill frequency={frequency} settings={settings} />}
        {note && <span className="muted-note">{note}</span>}
      </div>
      <div className="tasks">
        {tasks.map((task) => (
          <TaskRow
            key={`${task.serviceCode}-${task.frequency?.label || task.frequency}`}
            task={task}
            settings={settings}
            onRateChange={onRateChange}
          />
        ))}
      </div>
    </div>
  );
};

const BreakdownRows = ({ rows = [] }) => (
  <div className="te-breakdown-card">
    {rows.map((row) => (
      <div className="te-breakdown-row" key={row.label}>
        <span className="swab" style={{ background: row.color }} />
        <span className="te-breakdown-label">
          {row.label}
          {row.areas != null && <span className="areas"> ({row.areas} areas)</span>}
          {row.math && <span className="areas"> {row.math}</span>}
        </span>
        <b>{row.sqft != null ? `${fmtNumber(row.sqft, 0)} SF` : `${fmtNumber(row.quantity, 0)} zones`}</b>
      </div>
    ))}
  </div>
);

const InferredPreEmergent = ({ service, settings }) => {
  const sub = service.sub || settings.subcontractors.preEmergent;
  return (
    <div className="te-subline">
      <div className="lbl">
        <span className="te-inferred-badge">Inferred</span>
        {fmtNumber(service.inference.totalAcres, 2)} acres of pre-emergent area
        <span className="from">- from takeoff area-types</span>
        <Button className="ghost mini">Edit assumptions</Button>
      </div>
      <BreakdownRows rows={service.inference.breakdown} />
      <div className="te-breakdown-total">
        <span>Total pre-emergent area</span>
        <b>{fmtNumber(service.inference.totalSF, 0)} SF / 43,560 = {fmtNumber(service.inference.totalAcres, 2)} ac</b>
      </div>
      <div className="te-sub-calc">
        <span className="vendor">{sub.vendorName || 'Vendor'}</span>
        <span className="num">{fmtNumber(service.inference.totalAcres, 2)} ac</span>
        <span className="op">x</span>
        <span className="num">{fmtCurrency(sub.unitRate)} / ac</span>
        <span className="op">=</span>
        <span className="num">{fmtCurrency(service.inference.perVisitCost)} / visit</span>
        <span className="op">(min {fmtCurrency(sub.minPerVisit)} - {service.inference.minApplied ? 'applied' : 'not applied'})</span>
        <span className="op">x</span>
        <FrequencyPill frequency={sub.frequency} settings={settings} />
        <span className="eq">= {fmtCurrency(service.inference.annualCost)}</span>
      </div>
      <div className="te-help">Vendor rate, divisors and minimums are set in Settings - Subcontractors.</div>
    </div>
  );
};

const InferredIrrigation = ({ service }) => {
  const inference = service.inference;
  if (!inference) return null;
  return (
    <div className="te-subline">
      <div className="lbl">
        <span className="te-inferred-badge">Inferred</span>
        {fmtNumber(inference.totalZones, 0)} zones to inspect
        <span className="from">- from takeoff counts</span>
        <Button className="ghost mini">Edit assumptions</Button>
      </div>
      <BreakdownRows rows={inference.breakdown} />
      <div className="te-breakdown-total">
        <span>Total zones</span>
        <b>{fmtNumber(inference.totalZones, 0)} zones x {fmtNumber(inference.minutesPerZone, 0)} min/zone</b>
      </div>
    </div>
  );
};

const ServiceBlock = ({ service, settings, onToggle, onRateChange }) => {
  const result = computeService(service, settings);
  const summary = service.delivery === 'subcontracted'
    ? `${fmtCurrency(result.cost)} / yr`
    : `${fmtNumber(result.hours, 1)} hrs/yr`;
  const routineNote = service.role === 'Maint' && service.routineTasks?.length
    ? `${fmtNumber(service.routineTasks.reduce((sum, task) => sum + (Number(task.hoursPerVisit) || 0), 0), 2)} manhours`
    : null;

  return (
    <div className={`te-svc ${roleClass[service.role] || ''} ${service.enabled ? '' : 'off'}`}>
      <div className="row">
        <div className="swatch" />
        <div className="name">
          {service.name || service.role}
          {service.delivery === 'subcontracted' && <span className="te-sub-badge">Subcontracted</span>}
        </div>
        <div className={`hrs ${service.delivery === 'subcontracted' ? 'sub-cost' : ''}`}>{summary}</div>
        <button
          type="button"
          aria-label={`Toggle ${service.name || service.role}`}
          className="toggle"
          onClick={() => onToggle(service.role)}
        />
      </div>
      {service.delivery === 'subcontracted' && service.inference && (
        <InferredPreEmergent service={service} settings={settings} />
      )}
      {service.role === 'Irrigation' && service.inference && (
        <InferredIrrigation service={service} />
      )}
      <FrequencyGroup
        title="Every visit"
        frequency="weekly"
        tasks={service.routineTasks}
        settings={settings}
        note={routineNote}
        onRateChange={onRateChange}
      />
      <FrequencyGroup
        title="Per application"
        tasks={service.periodicTasks}
        settings={settings}
        onRateChange={onRateChange}
      />
    </div>
  );
};

// ── exported panel ───────────────────────────────────────────────────────────

export default function ScopePanel({ services = [], settings, onToggleRole, onRateChange, totalLaborHours }) {
  return (
    <div className="te-panel sf-scope-panel" id="scope">
      <div className="te-panel-head">
        <span className="te-panel-title">Scope &amp; Frequency</span>
        <span className="te-panel-sub">
          Edit frequency to update hours · Annual labor — <b>{fmtNumber(totalLaborHours, 1)} hrs</b>
        </span>
      </div>
      {services.map((service) => (
        <ServiceBlock
          key={service.role}
          service={service}
          settings={settings}
          onToggle={onToggleRole}
          onRateChange={onRateChange}
        />
      ))}
    </div>
  );
}
